<script setup lang="ts">
import ModalDialogV2 from '@/components/common/ModalDialogV2.vue';
import { useAuthStore } from '@/stores/auth.store';
import { useDebugStore } from '@/stores/debug.store';
import { useOrderSummaryStore } from '@/stores/orderSummary.store';
import { storeToRefs } from 'pinia';
import JsonViewer from 'vue-json-viewer';
import { useQuery } from '@tanstack/vue-query';
import { cxmService } from '@/services/cxm.service';

const route = useRoute();
const debugStore = useDebugStore();
const { enabled } = storeToRefs(debugStore);

const authStore = useAuthStore();
const orderSummaryStore = useOrderSummaryStore();

const isOpen = ref(false);

const offerSlugFromUrl = computed(() =>
  typeof route.query.offer === 'string' ? route.query.offer : null,
);

const { data: offerData, isLoading: isOfferLoading } = useQuery({
  queryKey: ['offer-by-slug-debug', offerSlugFromUrl],
  queryFn: () => cxmService.getOfferBySlug(offerSlugFromUrl.value!),
  select: (response) => response?.data?.data ?? null,
  enabled: computed(() => !!offerSlugFromUrl.value),
  refetchOnWindowFocus: false,
});

const userPayload = computed(() => authStore.user ?? null);
const accountPayload = computed(() => authStore.currentAccount ?? null);
const orderPayload = computed(
  () => orderSummaryStore.orderSummary ?? authStore.currentOrder ?? null,
);

const offerPayload = computed(() => ({
  slugFromUrl: offerSlugFromUrl.value,
  isLoading: isOfferLoading.value,
  offer: offerData.value ?? null,
}));
</script>

<template>
  <div v-if="enabled" class="fixed bottom-6 left-6 z-[90]">
    <button
      type="button"
      class="flex size-12 items-center justify-center rounded-full bg-brand-primary text-white shadow-lg transition hover:scale-[1.03] focus:outline-none focus:ring-2 focus:ring-brand-primary/40"
      @click="isOpen = true"
      aria-label="Open debug inspector"
    >
      <IconifyIcon icon="heroicons:bug-ant-20-solid" class="size-6" />
    </button>
  </div>

  <ModalDialogV2 v-model="isOpen" size="7xl" title="Debug Context" :hideActions="true">
    <div class="grid gap-6 lg:grid-cols-3">
      <section class="space-y-2">
        <h4 class="text-sm font-semibold uppercase tracking-wide text-semantic-text-secondary">
          User
        </h4>
        <JsonViewer :value="userPayload" :expand-depth="2" copyable boxed sort />
      </section>
      <section class="space-y-2">
        <h4 class="text-sm font-semibold uppercase tracking-wide text-semantic-text-secondary">
          Account
        </h4>
        <JsonViewer :value="accountPayload" :expand-depth="2" copyable boxed sort />
      </section>
      <section class="space-y-2">
        <h4 class="text-sm font-semibold uppercase tracking-wide text-semantic-text-secondary">
          Order
        </h4>
        <JsonViewer :value="orderPayload" :expand-depth="2" copyable boxed sort />
      </section>
      <section class="space-y-2">
        <h4 class="text-sm font-semibold uppercase tracking-wide text-semantic-text-secondary">
          Offer (from URL)
        </h4>
        <JsonViewer :value="offerPayload" :expand-depth="2" copyable boxed sort />
      </section>
    </div>
  </ModalDialogV2>
</template>
